/**
 * Free print in the NORMAL quick add (collection cards, product recommendations, drawer eye).
 *
 * When the cart has unlocked a free print that is not yet spent, the quick add opens on the locked
 * free ("medium") size, shows it at 0.00 with the real price struck through, and the button reads
 * "Add free print to cart" — the same thing the bundle builder's quick view does in free mode
 * (bundle-builder.js _qvSetupFreeMode / _qvApplyFreePricing). Display only: the print is added as a
 * plain variant, and the Shopify discount decides what is actually zeroed, exactly as for the
 * drawer recommendation cards.
 *
 * - Free state (snippets/wd-free-print-state.liquid, the same rule as the drawer recommendations) is
 *   printed into the page, so it is known before the first tap, and re-read from
 *   /cart?view=free-print in the background after every cart:update / cart:updated.
 * - The switch to the free size happens behind the modal's loader, so the shopper sees one load
 *   and lands on the free size, not the default size followed by a jump.
 * - Only unframed selections at a locked size read as free. A framed selection keeps the real price.
 * - Pack mode (data-bb-pack) is the bundle builder's own and is left alone.
 * - Fail open: any error leaves the normal quick add.
 */

const FREE_ATTR = 'data-wd-free';

let token = 0;
let observer = null;

let statePromise = Promise.resolve((() => {
  try {
    const el = document.getElementById('wd-free-print-state');
    return el ? JSON.parse(el.textContent) : null;
  } catch (e) {
    return null;
  }
})());
const refreshState = () => {
  statePromise = fetch('/cart?view=free-print', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
};
document.addEventListener('cart:update', refreshState);
document.addEventListener('cart:updated', refreshState);

function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    try { return window.Shopify.formatMoney(cents, window.mrk_money_format); } catch (e) { /* fall through */ }
  }
  const fmt = window.mrk_money_format || '${{amount}}';
  const amount = (Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

// Size matching mirrors bundle-builder.js _normSize / _freeKeywords / _sizeMatchesKeyword and
// custom-recs-product.liquid: keywords in priority order, matched either way round after normalising.
const normSize = (s) => String(s || '').toLowerCase().replace(/[\s"”“′″()]/g, '').replace(/×/g, 'x');
function keywords(src) {
  const seen = new Set();
  return String(src || '').split(',').map(normSize).filter((k) => {
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
const matches = (size, kw) => {
  const n = normSize(size);
  return !!n && !!kw && (n.indexOf(kw) > -1 || kw.indexOf(n) > -1);
};
const isFreeSize = (size, kws) => !!size && kws.some((kw) => matches(size, kw));

// Visible size radios only — a CSS-hidden option group must never be checked (same guard as
// bundle-builder.js _qvArmVisible).
function visible(el) {
  const field = el.closest('fieldset, .variant-option');
  return !field || field.getClientRects().length > 0;
}
const sizeRadios = (root) => [...root.querySelectorAll('.mrk-size-option input')].filter(visible);
const radioSize = (r) => r.getAttribute('data-value') || r.value || '';

// Selected size + whether a frame is chosen. Same sources as bundle-builder.js _qvSelection, in the
// same order: the print's own frame swatches, a real child-frame colour, the colour mrk.js stamps in
// .mrk-frame-color, then #mrk-main-variant-data.
const UNFRAMED = /unframed|no frame/i;
function selection(root) {
  const checked = sizeRadios(root).find((r) => r.checked);
  let size = checked ? radioSize(checked) : '';
  let frame = '';
  const own = [...root.querySelectorAll(
    'variant-picker .variant-option--swatches input:checked, variant-picker .mrk-swatches-slider-wrapper input:checked'
  )].find(visible);
  if (own) frame = own.getAttribute('data-value') || own.value || '';
  if (!frame || UNFRAMED.test(frame)) {
    const real = [...root.querySelectorAll('.mrk-frame-switcher input:checked')].find((n) => {
      const fc = (n.getAttribute('frame-color') || '').trim();
      return fc && !UNFRAMED.test(fc);
    });
    const stamped = root.querySelector('.mrk-frame-color');
    const v = stamped ? String(stamped.value || '').trim() : '';
    if (real) frame = real.value || real.getAttribute('frame-color') || frame;
    else if (v && !UNFRAMED.test(v)) frame = v;
  }
  if (!size || (!frame && !root.querySelector('frame-variant-picker'))) {
    try {
      const el = root.querySelector('#mrk-main-variant-data');
      const v = el ? JSON.parse(el.textContent || '{}') : null;
      if (v) {
        if (!size) size = v.option1 || '';
        if (!frame) frame = v.option2 || '';
      }
    } catch (e) { /* keep what the pickers gave us */ }
  }
  return { size, framed: !!(frame && !UNFRAMED.test(frame)) };
}

// Ownership tracking, as in bundle-builder.js _qvApplyFreePricing: mrk.js repaints these nodes on
// every size/frame change, so each write stamps the exact text it wrote. A node whose text no longer
// matches the stamp belongs to mrk.js again and its text is the real value.
const isOurs = (el) => el.dataset.wdFreeSet !== undefined && el.textContent.trim() === el.dataset.wdFreeSet;
const capture = (el, key) => { if (!isOurs(el)) el.dataset[key] = el.textContent; };
function write(el, text) {
  if (el.textContent.trim() === text) return;
  el.textContent = text;
  el.dataset.wdFreeSet = text;
}
function restore(el, key) {
  if (isOurs(el) && el.dataset[key] !== undefined) el.textContent = el.dataset[key];
  delete el.dataset[key];
  delete el.dataset.wdFreeSet;
}

// Show `zero` on a price node, with the real price struck through in its compare node. A print that
// is on sale keeps its pre-sale compare-at. Otherwise the compare node gets the real price: it is
// either blank, or hidden by mrk.js updatePrices because there is no discount.
function zeroPrice(price, compare, zero) {
  if (price.textContent.trim() !== zero) capture(price, 'wdFreeReal');
  if (compare) {
    if (!isOurs(compare)) {
      compare.dataset.wdFreeOrig = compare.textContent;
      compare.dataset.wdFreeDisplay = compare.style.display;
    }
    const real = (price.dataset.wdFreeReal || '').trim();
    const orig = compare.dataset.wdFreeOrig.trim();
    const onSale = compare.dataset.wdFreeDisplay !== 'none' && orig && orig !== real;
    if (real && !onSale) write(compare, real);
    compare.style.display = '';
  }
  write(price, zero);
}
function restorePrice(price, compare) {
  restore(price, 'wdFreeReal');
  if (!compare) return;
  if (compare.dataset.wdFreeDisplay !== undefined) {
    if (isOurs(compare)) compare.style.display = compare.dataset.wdFreeDisplay;
    delete compare.dataset.wdFreeDisplay;
  }
  restore(compare, 'wdFreeOrig');
}

function apply(root, state) {
  const kws = state.kws;
  const zero = formatMoney(0);
  const { size, framed } = selection(root);
  const free = !framed && isFreeSize(size, kws);

  // Size list: every locked-size row reads 0.00 while the selection is unframed, whichever size is
  // selected. A framed selection is never free, so every row gets its real price back.
  root.querySelectorAll('.mrk-size-item').forEach((item) => {
    const title = item.querySelector('.mrk-size-item-title');
    const price = item.querySelector('.mrk-size-price');
    if (!title || !price) return;
    const compare = item.querySelector('.mrk-size-compare-at-price');
    if (!framed && isFreeSize(title.textContent, kws)) zeroPrice(price, compare, zero);
    else restorePrice(price, compare);
  });

  // Main price and the price inside the button. Both are .mrk-main-price — mrk.js updatePrices
  // writes every one of them.
  root.querySelectorAll('.mrk-main-price').forEach((price) => {
    const compare = price.closest('[ref="priceContainer"]')?.querySelector('.mrk-main-compare-price') || null;
    if (free) zeroPrice(price, compare, zero);
    else restorePrice(price, compare);
  });

  // Button label. product-form.js morphs the button on a variant change, which puts the original
  // text back — so the original is re-read whenever the text is not ours.
  root.querySelectorAll('.add-to-cart-text__content').forEach((label) => {
    if (free) {
      if (label.textContent.trim() !== state.cta) {
        capture(label, 'wdFreeLabel');
        write(label, state.cta);
      }
    } else {
      restore(label, 'wdFreeLabel');
    }
  });
}

const loader = () => document.querySelector('#quick-add-dialog [data-quick-add-loader]');
let releaseLoader = null;

// Check the locked free size (first keyword hit, same priority as _qvPreselectFreeSize) and fire
// change so the buy-box re-renders on that variant. The re-render is a section fetch, so the loader
// stays up until the buy-box has updated (variant:update), with a cap so it can never stick.
function preselect(root, kws) {
  const radios = sizeRadios(root);
  let locked = null;
  for (const kw of kws) {
    locked = radios.find((r) => matches(radioSize(r), kw));
    if (locked) break;
  }
  if (!locked || locked.checked) return;
  const el = loader();
  if (el) {
    el.hidden = false;
    let timer = 0;
    const done = () => {
      clearTimeout(timer);
      document.removeEventListener('variant:update', done);
      releaseLoader = null;
      el.hidden = true;
    };
    timer = setTimeout(done, 3000);
    document.addEventListener('variant:update', done);
    releaseLoader = done;
  }
  locked.checked = true;
  locked.dispatchEvent(new Event('change', { bubbles: true }));
}

function stop(dialog) {
  token++;
  if (observer) { observer.disconnect(); observer = null; }
  releaseLoader?.();
  dialog?.removeAttribute(FREE_ATTR);
}

// Free prints are never canvas (bundle-builder.js _productFreeEligible). The product type is not in
// the quick-view markup, so read it from /products/<handle>.js — started on pointerdown, so it has
// usually landed by the time the modal is ready. Cached per URL.
const canvasCache = new Map();
const productPath = (url) => {
  try { return new URL(url, window.location.origin).pathname.replace(/\/$/, ''); } catch (e) { return ''; }
};
function isCanvas(url) {
  const path = productPath(url);
  if (!path) return Promise.resolve(false);
  if (/-canvas$/i.test(path)) return Promise.resolve(true);
  if (!canvasCache.has(path)) {
    canvasCache.set(path, fetch(path + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => !!(p && /canvas/i.test(p.type || '')))
      .catch(() => false));
  }
  return canvasCache.get(path);
}

async function start(root) {
  const dialog = document.getElementById('quick-add-dialog');
  stop(dialog);
  if (!dialog || dialog.hasAttribute('data-bb-pack')) return;
  const mine = token;

  const data = await statePromise;
  if (mine !== token || !data || !data.free) return;
  const kws = keywords(data.sizes);
  if (!kws.length || !sizeRadios(root).some((r) => isFreeSize(radioSize(r), kws))) return;
  const url = root.querySelector('product-form-component[data-product-url]')?.getAttribute('data-product-url') || '';
  if (await isCanvas(url)) return;
  if (mine !== token || dialog.hasAttribute('data-bb-pack')) return;

  const state = { kws, cta: String(data.cta || 'Add free print to cart') };
  dialog.setAttribute(FREE_ATTR, '');
  preselect(root, kws);
  apply(root, state);

  // mrk.js rewrites the prices after every size, frame and framing-service change, several of them
  // after async fetches that dispatch no event of their own. Watch the modal content (only while it
  // is open in free mode) and re-apply. apply() writes only when a value differs, so this settles
  // after one pass.
  let queued = false;
  observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      if (mine === token && dialog.hasAttribute(FREE_ATTR)) apply(root, state);
    });
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true });
}

document.addEventListener('drool:quick-add-ready', (event) => {
  const root = event.target;
  if (!(root instanceof HTMLElement)) return;
  start(root).catch((error) => {
    window.droolReportError?.('wd-quick-add-free', String(error && error.message || error));
  });
});

// Start the canvas lookup when a quick add button is pressed, alongside quick-add.js's own buy-box
// warm-up. Only while a free print is on offer — otherwise nothing is fetched.
document.addEventListener('pointerdown', (event) => {
  const button = event.target instanceof Element && event.target.closest('.mrk-quick-add-btn');
  const url = button ? button.getAttribute('data-product-url') || '' : '';
  if (url) statePromise.then((data) => { if (data && data.free) isCanvas(url); });
}, { capture: true, passive: true });

// The native <dialog> close event does not bubble, so listen in the capture phase.
document.addEventListener('close', (event) => {
  if (event.target instanceof HTMLDialogElement && event.target.closest('#quick-add-dialog')) {
    stop(document.getElementById('quick-add-dialog'));
  }
}, true);
